import { Dropdown, MenuProps, Row } from "antd";
import { EyeOutlined, EllipsisOutlined } from "@ant-design/icons";
import { useCallback, useMemo } from "react";
import { usePermissions } from "../../../../../../../lib/hooks/usePermissions";
import { useRecalculate } from "../hooks/useRecalculate";
import { useDelete } from "../hooks/useDelete";

type TableActionsProps = {
  id: string;
  isActionsDisabled: boolean;
  setPaymentId: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setIsActionsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  reloadData: () => void;
};

export const TableActions = ({
  id,
  isActionsDisabled,
  setPaymentId,
  setIsActionsDisabled,
  setOpenEditMode,
  reloadData,
}: TableActionsProps) => {
  const { onDeleteBtnClick } = useDelete(reloadData);

  const { hasAccessByController } = usePermissions("PaymentHistories");

  const { onRecalculateClick } = useRecalculate(
    reloadData,
    setIsActionsDisabled,
  );

  const onDelete = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      onDeleteBtnClick(e.currentTarget.id);
    },
    [onDeleteBtnClick],
  );

  const onView = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      setPaymentId(e.currentTarget.id);
    },
    [setPaymentId],
  );

  const onEdit = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      setPaymentId(e.currentTarget.id);
      setOpenEditMode(true);
    },
    [setOpenEditMode, setPaymentId],
  );

  const onRecalculate = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      onRecalculateClick(e.currentTarget.id);
    },
    [onRecalculateClick],
  );

  const items: MenuProps["items"] = useMemo(() => {
    const val: MenuProps["items"] = [];

    hasAccessByController("Modify") &&
      val.push({
        key: "1",
        label: (
          <div id={id} onClick={onEdit}>
            Edit Payment History
          </div>
        ),
      });

    hasAccessByController("Recalculate") &&
      val.push({
        key: "2",
        label: (
          <div id={id} onClick={onRecalculate}>
            Recalculate YTD Amounts
          </div>
        ),
      });

    hasAccessByController("Delete") &&
      val.push({
        key: "3",
        label: (
          <div id={id} onClick={onDelete}>
            Delete Payment History
          </div>
        ),
      });

    return val;
  }, [hasAccessByController, id, onDelete, onEdit, onRecalculate]);

  return (
    <Row className="ml-1 ant-table-actions">
      <EyeOutlined
        className="ant-table-btn"
        id={id}
        onClick={onView}
        disabled={isActionsDisabled}
      />
      {items.length > 0 && (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          disabled={isActionsDisabled}
          overlayClassName="payment-hist-dropdown"
        >
          <EllipsisOutlined
            className="ant-table-btn"
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      )}
    </Row>
  );
};
